import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { OnDestroyHookDirective } from '../common/hooks';
import {
  IBillingDocument,
  IBillingAccount,
  IOdbBillCategoryDetailSection,
  IOdbBillCategoryDetailSectionItem,
} from '@dch/billing-business-logic';
import { SelectedOdbDetailsInfo } from '../models/pii.model';
import {
  ODB_DETAILS_SESSION_DATA_KEY,
  UtilsService,
  isDocumentLesserThan12MonthsOld,
} from '../services/utils.service';
import { OVERVIEW_PAGE } from '../config/my-bill.constants';
import { Store } from '@ngrx/store';
import { merge } from 'rxjs';
import { filter, tap } from 'rxjs/operators';
import { ApplicationState } from 'src/app/state/reducers';
import {
  getOdbUsageLevel4Request,
  getSortedOdbUsageByColumn,
} from 'src/app/state/actions';
import {
  selectOdbUsageLevel4ByDocumentReferenceAndIdentifierSelector,
  selectOdbUsageLevel4SortingInfo,
} from 'src/app/state/selectors/odb-usage-level-4.selectors';
import { ICdrDetails } from '../models/odb-usage-level-4.model';
import {
  SortingColumnType,
  SortingOrderType,
} from '../models/odb-usage-l4.model';
import {
  IOdbUsageLevelFourPaginationSizeOption,
  IOdbUsageLevelFourTableEvents,
} from './table-pagination/odb-usage-level-four-table-pagination.component';
import { IODBLevelFourUsageTableColumnId } from './table-header/odb-usage-level-four-table-header.component';
import { TranslationService } from '@dch/ng-common';
import { BillingAnalyticsService } from '../services/billing-analytics.service';
import { selectCustomerAccount } from 'src/app/state/selectors/customer-account.selector';
import { MessageBoxTypesEnum } from '@dch/pxs-angular-design-system/src/lib/messages/message-box';
import { getExtraCostConfigFileRequest } from 'src/app/state/actions';
import {
  selectExtraCostConfigFile,
  selectExtraCostInfoPagesByProductIds,
} from 'src/app/state/selectors/extra-cost.selectors';

@Component({
  selector: 'my-bill-odb-usage-level-four-page',
  templateUrl: './odb-usage-level-four-page.component.html',
  standalone: false,
})
export class OdbUsageLevelFourPageComponent
  extends OnDestroyHookDirective
  implements OnInit
{
  readonly customerAccount$ = this.store.select(selectCustomerAccount);
  readonly extraCostConfigFile$ = this.store.select(selectExtraCostConfigFile);

  constructor(
    private router: Router,
    private utilsService: UtilsService,
    private store: Store<ApplicationState>,
    private translationService: TranslationService,
    private billingAnalyticsService: BillingAnalyticsService
  ) {
    super();
  }

  selectedOdbDetailsInfo?: SelectedOdbDetailsInfo;
  documentReference?: string;
  billingAccount?: IBillingAccount;
  isFetching: boolean;
  billingDocument?: IBillingDocument;
  hasDataAvailable: boolean;
  section: IOdbBillCategoryDetailSection;
  selectedSectionProductSplitIdentifier: string;
  selectedItem: IOdbBillCategoryDetailSectionItem;
  cdrDetails: ICdrDetails[];
  hasError: boolean;
  timeStampSortingOrder: SortingOrderType;
  durationSortingOrder: SortingOrderType;
  amountSortingOrder: SortingOrderType;
  destinationSortingOrder: SortingOrderType;

  currentPageNumber: number = 1;
  currentPageSize: IOdbUsageLevelFourPaginationSizeOption = 10;
  cdrDetailsDisplayed: ICdrDetails[];
  selectedColumnIds: IODBLevelFourUsageTableColumnId[];
  billableIdentifierNumber: string;
  selectedSectionItemIdentifier: string;

  messageBoxTypeError = MessageBoxTypesEnum.Error;
  messageBoxTypeInformation = MessageBoxTypesEnum.Information;
  messageBoxTitle: string;
  messageBoxContent: string;
  errorMessage: string;
  errorMessageTitle: string;
  vatAmountTranslation: string;
  isFixedInternetUsage: boolean;
  hasPrivacyOn: boolean;
  isDocumentOlderThan12Months: boolean;
  hasPrivacyFlagOn: boolean;
  hasExplanationInfoMessage = false;

  readonly selectOdbUsageLevel4SortingOrder$ = this.store.select(
    selectOdbUsageLevel4SortingInfo
  );

  ngOnInit() {
    this.selectedOdbDetailsInfo =
      this.utilsService.getInfoFromSessionStorage<SelectedOdbDetailsInfo>(
        ODB_DETAILS_SESSION_DATA_KEY
      );
    this.billingAccount = this.selectedOdbDetailsInfo?.billingAccount;
    this.documentReference =
      this.selectedOdbDetailsInfo?.billDocument?.reference;
    if (
      this.selectedOdbDetailsInfo !== undefined &&
      this.billingAccount !== undefined &&
      this.documentReference !== undefined
    ) {
      this.hasDataAvailable = true;
      this.billingDocument = this.selectedOdbDetailsInfo.billDocument;
      this.section = this.selectedOdbDetailsInfo.selectedSection;
      this.selectedItem = this.selectedOdbDetailsInfo.selectedItem;

      this.selectedSectionItemIdentifier =
        this.selectedOdbDetailsInfo.selectedSectionItemIdentifier;
      this.billableIdentifierNumber = Array.prototype.map
        .call(this.selectedItem.technicalIdentifier, (technicalIdentifier) =>
          encodeURIComponent(technicalIdentifier)
        )
        .toString();
      this.selectedSectionProductSplitIdentifier =
        this.selectedOdbDetailsInfo.selectedSectionProductSplitIdentifier;
      if (isDocumentLesserThan12MonthsOld(this.billingDocument.documentDate)) {
        this.dispatchOdbUsageLevel4Request();
        this.subscribeToOdbUsageLevel4();
        this.subscribeToOdbUsageLevel4SortingInfo();
      } else {
        this.isDocumentOlderThan12Months = true;
        this.addMessageBoxInfo(
          'bills.cover.usageLevel4.documentOlderThan12Months.title',
          this.translationService.getTranslatedValueFor(
            'bills.cover.usageLevel4.documentOlderThan12Months.message'
          )
        );
      }
      this.getPageImpressionTagging();
    } else {
      this.router.navigate([OVERVIEW_PAGE]);
    }
  }

  sortColumn(sortingColumn: SortingColumnType) {
    this.store.dispatch(
      getSortedOdbUsageByColumn({
        documentReference: this.documentReference,
        sectionOrItemIdentifier: this.selectedSectionItemIdentifier,
        sortingColumn,
      })
    );
  }

  private dispatchOdbUsageLevel4Request() {
    this.store.dispatch(
      getOdbUsageLevel4Request({
        documentReference: this.documentReference,
        billableIdentifierNumber: this.billableIdentifierNumber,
        sectionOrItemIdentifier: this.selectedSectionItemIdentifier,
        paNumber: this.billingAccount?.accountNumber,
      })
    );
  }

  private subscribeToOdbUsageLevel4() {
    this.addSubscription(
      this.store
        .select(
          selectOdbUsageLevel4ByDocumentReferenceAndIdentifierSelector(
            this.documentReference,
            this.selectedSectionItemIdentifier
          )
        )
        .pipe(
          filter((odbUsageLevel4State) => odbUsageLevel4State !== undefined),
          tap((odbUsageLevel4State) => {
            this.cdrDetails = odbUsageLevel4State.result?.cdrDetails;
            this.hasError = odbUsageLevel4State.hasError;
            this.isFetching = odbUsageLevel4State.isFetching;
            this.hasPrivacyOn = false;
            this.messageBoxContent = '';
            this.messageBoxTitle = '';

            this.isFixedInternetUsage =
              !this.hasError && !this.isFetching && this.hasTimeStampMissing();
            if (!this.isFixedInternetUsage) {
              this.translationService.getTranslatedValueFor(
                'bills.cover.usageLevel4.privacy.flagOn'
              ) === 'true' &&
                odbUsageLevel4State.result?.privacyActive &&
                Boolean(this.shouldShowDestinationColumn()) &&
                this.addPrivacyInfo();

              this.computeDisplayedCdrDetails();
            } else {
              this.addMessageBoxInfo(
                'bills.cover.usageLevel4.fixedInternetUsageInfo.title',
                this.translationService.getTranslatedValueFor(
                  'bills.cover.usageLevel4.fixedInternetUsageInfo.message'
                )
              );
            }

            if (
              this.selectedItem &&
              Number(this.selectedItem.amount) > 0 &&
              this.selectedItem.productId &&
              !this.hasError
            ) {
              this.getExplanationInfoPageFromExtraCostCsvByProductId(
                this.selectedItem.productId
              );
            }

            if (
              this.hasError &&
              this.translationService.getTranslatedValueFor(
                'bills.cover.usageLevel4.privacy.code'
              ) === odbUsageLevel4State.code
            ) {
              this.addPrivacyInfo();
            } else if (this.hasError) {
              this.addErrorMessageBox(
                'bills.cover.usageLevel4.errorAlert.title',
                this.translationService.getTranslatedValueFor(
                  'bills.cover.usageLevel4.errorAlert.message'
                )
              );
            }
          })
        )
        .subscribe()
    );
  }

  addPrivacyInfo() {
    this.hasPrivacyOn = true;
    this.addMessageBoxInfo(
      'bills.cover.usageLevel4.privacy.title',
      this.translationService
        .getTranslatedValueFor('bills.cover.usageLevel4.privacy.message')
        .replace(/{{msisdn}}/g, this.selectedSectionProductSplitIdentifier)
    );
  }

  hasTimeStampMissing() {
    return !this.cdrDetails?.some((detail) => detail.timeStamp);
  }

  shouldShowDestinationColumn() {
    return this.cdrDetails?.some((detail) => detail.destination);
  }

  shouldShowDurationColumn() {
    return this.cdrDetails?.some((detail) => detail.duration);
  }
  hasDurationTypeInSec() {
    return this.cdrDetails?.some(
      (detail) => detail.duration && detail.duration.type === 'sec'
    );
  }

  private subscribeToOdbUsageLevel4SortingInfo() {
    this.addSubscription(
      this.selectOdbUsageLevel4SortingOrder$
        .pipe(
          filter(
            (odbUsageLevel4SortingState) =>
              odbUsageLevel4SortingState !== undefined
          ),
          tap((odbUsageLevel4SortingState) => {
            this.timeStampSortingOrder =
              odbUsageLevel4SortingState.sortingColumn === 'timeStamp'
                ? odbUsageLevel4SortingState.sortingOrder
                : 'none';
            this.durationSortingOrder =
              odbUsageLevel4SortingState.sortingColumn === 'duration'
                ? odbUsageLevel4SortingState.sortingOrder
                : 'none';
            this.amountSortingOrder =
              odbUsageLevel4SortingState.sortingColumn === 'amount'
                ? odbUsageLevel4SortingState.sortingOrder
                : 'none';
            this.destinationSortingOrder =
              odbUsageLevel4SortingState.sortingColumn === 'destination'
                ? odbUsageLevel4SortingState.sortingOrder
                : 'none';
          })
        )
        .subscribe()
    );
  }

  private getExplanationInfoPageFromExtraCostCsvByProductId(productId: string) {
    this.addSubscription(
      merge(
        this.extraCostConfigFile$.pipe(
          filter(
            (config) =>
              (!config.hasError &&
                config.extraCostConfig === null &&
                !config.isFetching) ||
              (config.hasError && config.extraCostConfig === null)
          ),
          tap(() => this.store.dispatch(getExtraCostConfigFileRequest()))
        ),
        this.extraCostConfigFile$.pipe(
          filter(
            (config) =>
              !config.hasError &&
              config.extraCostConfig !== null &&
              !config.extraCostConfig.hasError
          ),
          tap((config) => {
            if (config.extraCostConfig) {
              this.setExplanationInfoMessageBySelectedInfoPage(productId);
            }
          })
        )
      ).subscribe()
    );
  }

  private setExplanationInfoMessageBySelectedInfoPage(productId: string) {
    this.addSubscription(
      this.store
        .select(selectExtraCostInfoPagesByProductIds([productId]))
        .pipe(
          filter((selectInfoPage) => selectInfoPage.length > 0),
          tap((selectInfoPage) => {
            if (selectInfoPage[0] === 'mmsInfoPage') {
              this.hasExplanationInfoMessage = true;
            }
          })
        )
        .subscribe()
    );
  }

  handleActions(event: IOdbUsageLevelFourTableEvents) {
    switch (event.name) {
      case 'changeCurrentPage':
        const { target: targetPageNumber } = event;
        this.currentPageNumber = targetPageNumber;
        break;
      case 'changePageSize':
        const { target: targetPageSize } = event;
        this.currentPageSize = targetPageSize;
        this.currentPageNumber = 1;
        break;
      case 'goToNextPage':
        this.currentPageNumber += 1;
        break;
      case 'goToPreviousPage':
        this.currentPageNumber -= 1;
        break;
      default:
        break;
    }
    this.computeDisplayedCdrDetails();
  }

  handleSelectedColumnsChange(columnIds: IODBLevelFourUsageTableColumnId[]) {
    this.selectedColumnIds = columnIds;
  }

  handleDownloadCSVError(hasDownloadCSVError: boolean) {
    if (hasDownloadCSVError) {
      this.addErrorMessageBox(
        'bills.cover.usageLevel4.errorAlert.downloadCSV.title',
        this.translationService.getTranslatedValueFor(
          'bills.cover.usageLevel4.errorAlert.downloadCSV.message'
        )
      );
    }
  }

  private addErrorMessageBox(msgTitleKey: string, msgBoxContent: string) {
    this.errorMessageTitle =
      this.translationService.getTranslatedValueFor(msgTitleKey);
    this.errorMessage = msgBoxContent;
  }

  private addMessageBoxInfo(msgTitleKey: string, msgBoxContent: string) {
    this.messageBoxTitle =
      this.translationService.getTranslatedValueFor(msgTitleKey);
    this.messageBoxContent = msgBoxContent;
  }

  private computeDisplayedCdrDetails() {
    const startIndex = (this.currentPageNumber - 1) * this.currentPageSize;
    this.cdrDetailsDisplayed = this.cdrDetails?.slice(
      startIndex,
      startIndex + this.currentPageSize
    );
  }

  private getPageImpressionTagging() {
    this.addSubscription(
      this.customerAccount$
        .pipe(
          filter((customerAccount) => customerAccount !== undefined),
          tap((customerAccount) => {
            this.billingAnalyticsService.reportPageImpressionEventWithPagePrefix(
              'detail.usage',
              this.documentReference,
              this.billingAccount.accountNumber,
              'mybill.detail.usage',
              'mybill.detail.usage.details.' + this.section.description,
              customerAccount.getSelectedCustomer().customerId,
              customerAccount.getSelectedCustomer().subsegment,
              customerAccount.globalId,
              customerAccount.mobile
            );
            const isResUser =
              customerAccount.getSelectedCustomer().subsegment === 'RES';
            const vatAmountKey = isResUser
              ? 'amountResExplanation'
              : 'amountExplanation';
            this.vatAmountTranslation =
              this.translationService.getTranslatedValueFor(
                `bills.cover.usageLevel4.${vatAmountKey}`
              );
          })
        )
        .subscribe()
    );
  }
}
